import { atom } from 'jotai';
import { atomWithObservable, atomFamily } from 'jotai/utils';
import { from } from 'rxjs';
import { createApiClient } from '@/services/api/httpClient';
import { createLongPollingObservable } from '@/utils/longPolling';
import { ActivitiesResponse, ActivitiesParams, Activity } from '@/features/activity/types';
import { TokenService } from '@/services/auth/tokenService';

const fallbackActivitiesResponse: ActivitiesResponse = {
  activities: [],
  lastEvaluatedKey: undefined,
};

// Basic activities atom for a specific client
export const activitiesAtom = atomFamily((params: ActivitiesParams) =>
  atom(async () => {
    const { clientId, lastEvaluatedKey, limit } = params;

    const tokenService = TokenService.getInstance();
    const authToken = await tokenService.getCurrentToken();

    if (!authToken) {
      throw new Error('Authentication required');
    }

    const apiClient = createApiClient(() => authToken);

    // Build query parameters
    const queryParams = new URLSearchParams({
      clientId,
      ...(lastEvaluatedKey && { lastEvaluatedKey }),
      ...(limit && { limit: limit.toString() }),
    });

    return apiClient.get<ActivitiesResponse>(
      `/activities?${queryParams.toString()}`,
    );
  }),
);

// Observable atom for real-time updates with long polling
export const activitiesObservableAtom = atomFamily((params: ActivitiesParams) =>
  atomWithObservable((_get) => {
    // Check if we have a valid token using TokenService
    const tokenService = TokenService.getInstance();
    const storedTokens = tokenService.getStoredTokens();

    if (!storedTokens || tokenService.isTokenExpired()) {
      return from(Promise.resolve(fallbackActivitiesResponse));
    }

    // Build query parameters
    const queryParams = new URLSearchParams({
      clientId: params.clientId,
      ...(params.lastEvaluatedKey && {
        lastEvaluatedKey: params.lastEvaluatedKey,
      }),
      ...(params.limit && { limit: params.limit.toString() }),
    });

    return createLongPollingObservable(
      `/activities?${queryParams.toString()}`,
      storedTokens.idToken,
      10000, // Poll every 10 seconds
      fallbackActivitiesResponse,
    );
  }),
);

// Atom for page-based pagination state
export const activitiesPaginationAtom = atom<{
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  loading: boolean;
  pageKeys: string[]; // Array of lastEvaluatedKeys for each page
}>({
  currentPage: 1,
  pageSize: 10,
  hasNextPage: false,
  loading: false,
  pageKeys: [], // Empty array means we're on the first page
});

// Atom for loading activities for a specific page
export const loadActivitiesPageAtom = atom(
  null,
  async (
    get,
    set,
    { clientId, page, limit = 10 }: { clientId: string; page: number; limit?: number },
  ) => {
    const tokenService = TokenService.getInstance();
    const authToken = await tokenService.getCurrentToken();

    if (!authToken) {
      throw new Error('Authentication required');
    }

    set(activitiesPaginationAtom, (prev) => ({ ...prev, loading: true }));

    try {
      const apiClient = createApiClient(() => authToken);
      const pagination = get(activitiesPaginationAtom);
      
      // Determine the lastEvaluatedKey based on the requested page
      let lastEvaluatedKey: string | undefined;
      if (page > 1 && pagination.pageKeys.length >= page - 1) {
        lastEvaluatedKey = pagination.pageKeys[page - 2];
      }

      const params = new URLSearchParams({
        clientId,
        limit: limit.toString(),
        ...(lastEvaluatedKey && { lastEvaluatedKey }),
      });

      const response = await apiClient.get<ActivitiesResponse>(
        `/activities?${params.toString()}`,
      );

      // Update page keys array
      const newPageKeys = [...pagination.pageKeys];
      if (response.lastEvaluatedKey && newPageKeys.length === page - 1) {
        newPageKeys.push(response.lastEvaluatedKey);
      }

      set(activitiesPaginationAtom, {
        currentPage: page,
        pageSize: limit,
        hasNextPage: !!response.lastEvaluatedKey,
        loading: false,
        pageKeys: newPageKeys,
      });

      return response;
    } catch (error) {
      set(activitiesPaginationAtom, (prev) => ({ ...prev, loading: false }));
      throw error;
    }
  },
);

// Atom to navigate to next page
export const goToNextPageAtom = atom(
  null,
  async (get, set, clientId: string) => {
    const pagination = get(activitiesPaginationAtom);
    if (!pagination.hasNextPage || pagination.loading) return;

    return set(loadActivitiesPageAtom, {
      clientId,
      page: pagination.currentPage + 1,
      limit: pagination.pageSize,
    });
  },
);

// Atom to navigate to previous page
export const goToPreviousPageAtom = atom(
  null,
  async (get, set, clientId: string) => {
    const pagination = get(activitiesPaginationAtom);
    if (pagination.currentPage <= 1 || pagination.loading) return;

    return set(loadActivitiesPageAtom, {
      clientId,
      page: pagination.currentPage - 1,
      limit: pagination.pageSize,
    });
  },
);

// Atom to go to a specific page
export const goToPageAtom = atom(
  null,
  async (get, set, { clientId, page }: { clientId: string; page: number }) => {
    const pagination = get(activitiesPaginationAtom);
    if (page < 1 || page === pagination.currentPage || pagination.loading) return;

    return set(loadActivitiesPageAtom, {
      clientId,
      page,
      limit: pagination.pageSize,
    });
  },
);

// Atom to reset pagination
export const resetActivitiesPaginationAtom = atom(null, (_, set) => {
  set(activitiesPaginationAtom, {
    currentPage: 1,
    pageSize: 10,
    hasNextPage: false,
    loading: false,
    pageKeys: [],
  });
});

// Selected activity atom - stores the currently selected activity
export const selectedActivityAtom = atom<Activity | null>(null);

// Callable atom to get a specific activity by ID
export const getActivityAtom = atom(
  null,
  async (_get, _set, { clientId, activityId }: { clientId: string; activityId: string }) => {
    const tokenService = TokenService.getInstance();
    const authToken = await tokenService.getCurrentToken();

    if (!authToken) {
      throw new Error('Authentication required');
    }

    const apiClient = createApiClient(() => authToken);

    const params = new URLSearchParams({
      clientId,
      activityId,
    });

    const response = await apiClient.get<Activity>(
      `/activities/${activityId}?${params.toString()}`,
    );

    return response;
  }
);

// Callable atom to set the selected activity
export const setSelectedActivityAtom = atom(
  null,
  (_get, set, activity: Activity | null) => {
    set(selectedActivityAtom, activity);
  }
);
